import { useTranslation } from 'react-i18next'
import { MapPin, Phone, Mail } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Container } from '@/components/ui/Container'

const certifications = [
  { name: 'ISO 9001', src: '/images/certifications/iso.jpg' },
  { name: 'ISO 14001', src: '/images/certifications/iso.jpg' },
  { name: 'PED', src: '/images/certifications/ped.jpg' },
  { name: 'IATF', src: '/images/certifications/iatf.jpg' },
  { name: 'ICM', src: '/images/ICM.png' },
]

const navLinks = [
  { key: 'about', labelKey: 'nav.about' },
  { key: 'processes', labelKey: 'nav.processes' },
  { key: 'innovation', labelKey: 'nav.innovation' },
  { key: 'products', labelKey: 'nav.products' },
  { key: 'sectors', labelKey: 'nav.sectors' },
  { key: 'sustainability', labelKey: 'nav.sustainability' },
  { key: 'contacts', labelKey: 'nav.contacts' },
] as const

export function Footer() {
  const { t } = useTranslation()

  return (
    <footer className="bg-primary-950 text-neutral-300" role="contentinfo">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 py-16">
          {/* Company */}
          <div>
            <img
              src="/images/PEL-logo.svg"
              alt="PEL Pintossi Emilio S.p.A."
              width={140}
              height={47}
              className="h-10 w-auto brightness-0 invert mb-4"
            />
            <p className="text-sm text-neutral-400 mb-4">
              {t('footer.tagline', 'Stampaggio a caldo e lavorazioni meccaniche di precisione dal 1960.')}
            </p>
            <address className="not-italic text-sm space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-neutral-500" />
                <span>Via Antonini, 123/125<br />25068 Sarezzo (BS), Italia</span>
              </div>
            </address>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.navigation')}
            </h3>
            <ul className="space-y-2">
              {navLinks.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-sm text-neutral-400 hover:text-white no-underline transition-colors"
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.products', 'Prodotti')}
            </h3>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li>{t('products.categories.ballValves', 'Valvole a sfera')}</li>
              <li>{t('products.categories.gasTaps', 'Rubinetti gas cucine')}</li>
              <li>{t('products.categories.steelFittings', 'Raccordi in acciaio')}</li>
              <li>{t('products.categories.gasWaterValves', 'Valvole reti gas/acqua')}</li>
              <li>{t('products.categories.turnedParts', 'Particolari torniti')}</li>
              <li>{t('products.categories.specialProductions', 'Produzioni speciali')}</li>
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              {t('footer.contacts')}
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="tel:+390308936011"
                  className="flex items-center gap-2 text-neutral-400 hover:text-white no-underline transition-colors"
                >
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  +39 030 8936011
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@pel.it"
                  className="flex items-center gap-2 text-neutral-400 hover:text-white no-underline transition-colors"
                >
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  sales@pel.it
                </a>
              </li>
            </ul>

            {/* Certifications */}
            <div className="mt-6">
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-3">
                {t('footer.certifications', 'Certificazioni')}
              </p>
              <div className="flex flex-wrap gap-3">
                {certifications.map((cert) => (
                  <img
                    key={cert.name}
                    src={cert.src}
                    alt={cert.name}
                    width={40}
                    height={40}
                    className="h-10 w-10 object-contain rounded bg-white p-1"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-800 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-500 text-center sm:text-left">
          <p>&copy; {new Date().getFullYear()} PEL Pintossi Emilio S.p.A.<br className="sm:hidden" /> P.IVA 00552900987 — {t('footer.allRightsReserved')}</p>
          <div className="flex items-center gap-4">
            <LocalizedLink to="privacy" className="hover:text-white no-underline transition-colors">
              {t('footer.privacy')}
            </LocalizedLink>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-preferences'))}
              className="hover:text-white transition-colors"
            >
              {t('footer.cookiePolicy')}
            </button>
          </div>
        </div>
      </Container>
    </footer>
  )
}
